import { CSSProperties } from 'react';
import { cn } from '../../../lib/utils';

// NOTE: the source DS object literal declared `confirmed` and `active` twice
// (once in their thematic group, once in the "pipeline stages" group). JS
// last-key-wins semantics resolve them to confirmed:'gold', active:'success'
// — TS forbids the literal duplicate keys, so those are declared once below
// with the winning (pipeline-stage) value, preserving identical behavior.
const TONE_MAP: Record<string, StatusTone> = {
  // gold — waiting on someone
  pending: 'gold', submitted: 'gold',
  // violet — active / in conversation / proposed match
  proposed: 'violet',
  // success — confirmed
  approved: 'success', accepted: 'success', paid: 'success', published: 'success',
  // crimson — rejected
  rejected: 'crimson', declined: 'crimson', failed: 'crimson', refunded: 'crimson',
  // pipeline stages — Inquiry(neutral) → Building(violet) → Confirmed(gold) → Active(success) → Completed(neutral)
  inquiry: 'neutral', building: 'violet', confirmed: 'gold', active: 'success', 'active-now': 'success',
  // neutral — terminal / inactive
  draft: 'neutral', inactive: 'neutral', complete: 'neutral', completed: 'neutral', archived: 'neutral',
};

// ACCENT drives background + border opacity. TEXT drives readable label colour.
const ACCENT: Record<StatusTone, string> = {
  neutral: 'var(--text-dim-rgb)',
  gold: 'var(--gold)',
  violet: 'var(--violet)',
  success: 'var(--success)',
  crimson: 'var(--crimson)',
};
const TEXT_COLOR: Record<StatusTone, string> = {
  neutral: 'rgb(var(--text-dim-rgb))',
  gold: 'rgb(var(--gold))',
  violet: 'rgb(var(--violet))',
  success: 'var(--success-fg)',   // theme-aware readable green (#2E6B23 light / #9EC98A dark)
  crimson: 'rgb(var(--crimson))',
};
// Per-tone background/border opacity — Building runs slightly stronger (.13/.26)
// than the rest of the pipeline (.12/.24), matching the style guide exactly.
const BG_OPACITY: Record<StatusTone, number> = { neutral: 0.12, gold: 0.12, violet: 0.13, success: 0.12, crimson: 0.12 };
const BORDER_OPACITY: Record<StatusTone, number> = { neutral: 0.24, gold: 0.24, violet: 0.26, success: 0.24, crimson: 0.24 };

/**
 * StatusBadge — pill with a leading dot for status/state. Colour is derived
 * semantically (gold=waiting, violet=active, success=confirmed, crimson=
 * rejected, neutral=terminal) so every pill across the product reads the same.
 */
export type StatusTone = 'neutral' | 'gold' | 'violet' | 'success' | 'crimson';

export interface StatusBadgeProps {
  /** Raw status string — auto-coloured (e.g. 'pending', 'accepted', 'declined'). */
  status?: string;
  /** Explicit tone override; takes precedence over `status`. */
  tone?: StatusTone;
  /** Display text. Defaults to `status`. */
  label?: string;
  /** Show the leading dot. Default true. */
  dot?: boolean;
  /** Render only the coloured dot (no pill chrome, no label). */
  dotOnly?: boolean;
  /**
   * Sizing role — Sekretly Design System §5.3 "pipeline stage badge"
   * (padding 4px 11px, font 11.5px, 6px dot; Inquiry/Building/Confirmed/
   * Active/Completed) vs §5.2 "plain status/approved badge" (padding
   * 5px 12px, font 11px, 5px dot; standalone status pills like Approved/
   * Paid/Draft). Default 'pipeline' — every current call site renders a
   * pipeline-shaped badge today, so this preserves existing output.
   */
  variant?: 'pipeline' | 'status';
  className?: string;
  style?: CSSProperties;
}

const SIZE = {
  pipeline: { padding: '4px 11px', fontSize: 'var(--fs-badge-pipeline)', dotSize: 6 },
  status: { padding: '5px 12px', fontSize: 'var(--fs-badge-status)', dotSize: 5 },
} as const;

export function StatusBadge({ status, tone, label, dot = true, dotOnly = false, variant = 'pipeline', className, style }: StatusBadgeProps) {
  const resolved = tone || (status ? (TONE_MAP[String(status).toLowerCase()] || 'neutral') : 'neutral');
  const raw = label != null ? label : (status || '');
  const text = String(raw).charAt(0).toUpperCase() + String(raw).slice(1);
  const a = ACCENT[resolved];
  // Neutral (Inquiry / Completed) is a flat surface pill, not an opacity tint.
  const isNeutral = resolved === 'neutral';
  // Confirmed reads as the theme-aware --accent (gold in dark, violet in light)
  // for text + dot, while its background/border stay literal gold per the guide.
  const isConfirmed = resolved === 'gold';
  const dotTextColor = isConfirmed ? 'var(--accent)' : `rgb(${a})`;
  const { padding, fontSize, dotSize } = SIZE[variant];
  if (dotOnly) {
    return (
      <span
        className={cn(className)}
        style={{ display: 'inline-block', width: dotSize, height: dotSize, borderRadius: '999px', background: dotTextColor, flexShrink: 0, ...style }}
      />
    );
  }
  return (
    <span
      className={cn(className)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        fontFamily: 'var(--font-sans)', fontSize, fontWeight: 500, whiteSpace: 'nowrap',
        borderRadius: 'var(--r-pill)', padding,
        color: isConfirmed ? 'var(--accent)' : TEXT_COLOR[resolved],
        background: isNeutral ? 'var(--surface)' : `rgb(${a} / ${BG_OPACITY[resolved]})`,
        border: isNeutral ? '1px solid var(--line)' : `1px solid rgb(${a} / ${BORDER_OPACITY[resolved]})`,
        ...style,
      }}
    >
      {dot ? <span style={{ width: dotSize, height: dotSize, borderRadius: '999px', background: dotTextColor, flexShrink: 0 }} /> : null}
      {text}
    </span>
  );
}

export default StatusBadge;
